// /quotes: every quote the company has built, newest activity first, with the
// pipeline at a glance (drafts, sent, viewed, accepted and the value won).
// Opening a row goes to the quote builder. docs/10-quote-engine.md §7.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, FilePlus2, Search } from "lucide-react";
import { useQuotesApi, type QuoteStatus, type QuoteSummary } from "../../quotes/api";
import { STATUS_LABELS, gbp, pct, when } from "../../quotes/format";
import { useQuoteAccess } from "../../quotes/Access";
import "../../quotes/quotes.css";

const FILTERS: [QuoteStatus | "all", string][] = [
  ["all", "All"],
  ["draft", "Drafts"],
  ["sent", "Sent"],
  ["viewed", "Viewed"],
  ["accepted", "Accepted"],
  ["declined", "Declined"],
];

export function QuoteStatusChip({ status, changed }: { status: QuoteStatus; changed?: boolean }) {
  return (
    <span className={`q-status q-status-${status}`} title={changed ? "Edited since it was sent: send it again to update the customer's copy" : undefined}>
      {STATUS_LABELS[status]}
      {changed ? " · edited" : ""}
    </span>
  );
}

export function QuotesPage() {
  const api = useQuotesApi();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<QuoteSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<QuoteStatus | "all">("all");
  const [search, setSearch] = useState("");
  const { access } = useQuoteAccess();

  useEffect(() => {
    api
      .listQuotes()
      .then(setQuotes)
      .catch((e: Error) => setError(e.message));
  }, [api]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const q of quotes ?? []) c[q.status] = (c[q.status] ?? 0) + 1;
    return c;
  }, [quotes]);
  const won = (quotes ?? []).filter((q) => q.status === "accepted").reduce((s, q) => s + (q.acceptance?.totals.gross ?? 0), 0);
  const answered = (counts.accepted ?? 0) + (counts.declined ?? 0);

  const shown = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return (quotes ?? []).filter((q) => {
      if (filter !== "all" && q.status !== filter) return false;
      const text = [q.reference, q.customer?.name, q.customer?.email, q.address?.line, q.summary?.optionName].join(" ").toLowerCase();
      return words.every((w) => text.includes(w));
    });
  }, [quotes, filter, search]);

  return (
    <div className="page q-page">
      <div className="page-head q-head">
        <div>
          <h1>Quotes</h1>
          <p>Proper, itemised quotes from your own catalogue and prices, sized from each home's heat loss. Send them by email or share the link; customers choose an option and accept online.</p>
        </div>
        <Link to="/quotes/new" className="btn btn-primary">
          <FilePlus2 size={16} aria-hidden="true" /> New quote
        </Link>
      </div>

      {error && <div className="flash flash-danger">{error}</div>}
      {access && !access.termsAccepted && (
        <div className="flash flash-info">
          <span>
            Before you send quotes, check your prices and accept the quote terms in <Link to="/quotes/settings">Quote settings</Link>.
          </span>
        </div>
      )}

      <div className="kpi-row q-kpis">
        <div className="card kpi">
          <div className="kpi-label">Drafts</div>
          <div className="kpi-value">{counts.draft ?? 0}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Sent, awaiting answer</div>
          <div className="kpi-value">{(counts.sent ?? 0) + (counts.viewed ?? 0)}</div>
          <div className="kpi-note">{counts.viewed ?? 0} opened by the customer</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Accepted</div>
          <div className="kpi-value">{counts.accepted ?? 0}</div>
          <div className="kpi-note">{answered ? `${pct(((counts.accepted ?? 0) / answered) * 100)} of answered quotes` : "No answers yet"}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Value accepted</div>
          <div className="kpi-value">{gbp(won)}</div>
          <div className="kpi-note">including VAT, before grants</div>
        </div>
      </div>

      <div className="dash-filters q-filters">
        <div className="ed-segmented" role="group" aria-label="Status">
          {FILTERS.map(([v, l]) => (
            <button key={v} type="button" className={v === filter ? "is-on" : ""} aria-pressed={v === filter} onClick={() => setFilter(v)}>
              {l}
              {v !== "all" && counts[v] ? ` (${counts[v]})` : ""}
            </button>
          ))}
        </div>
        <label className="icon-input q-search">
          <Search size={16} aria-hidden="true" />
          <input className="input" placeholder="Search customer, address or reference" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>

      {quotes === null && !error ? (
        <p className="muted">Loading…</p>
      ) : quotes && quotes.length === 0 ? (
        <div className="card empty">
          <h2>No quotes yet</h2>
          <p>Start from a postcode: we look up the home's EPC, work out its heat loss and price your packages for it.</p>
          <p>
            <Link to="/quotes/new" className="btn btn-primary">
              <FilePlus2 size={16} aria-hidden="true" /> Create your first quote
            </Link>
          </p>
          <p className="muted">
            First time? Check your <Link to="/quotes/catalogue">catalogue</Link> and <Link to="/quotes/settings">quote settings</Link> (prices, margins, packages).
          </p>
        </div>
      ) : (
        <div className="card table-scroll">
          <table className="stats-table q-table">
            <thead>
              <tr>
                <th>Quote</th>
                <th>Customer</th>
                <th>Address</th>
                <th>Status</th>
                <th className="num">Price</th>
                <th className="num">Margin</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((q) => (
                <tr key={q.quoteId} className="q-row" onClick={() => navigate(`/quotes/${q.quoteId}`)}>
                  <td>
                    <Link to={`/quotes/${q.quoteId}`} onClick={(e) => e.stopPropagation()}>
                      {q.reference}
                    </Link>
                  </td>
                  <td>{q.customer?.name || <span className="muted">No name yet</span>}</td>
                  <td className="q-addr">{q.address?.line}</td>
                  <td>
                    <QuoteStatusChip status={q.status} changed={q.changedSinceSent} />
                    {q.publish?.views ? (
                      <span className="q-views" title={`Last opened ${when(q.publish.lastViewedAt)}`}>
                        <Eye size={13} aria-hidden="true" /> {q.publish.views}
                      </span>
                    ) : null}
                  </td>
                  <td className="num">
                    {q.status === "accepted" && q.acceptance ? gbp(q.acceptance.totals.payable) : q.summary?.payable != null ? gbp(q.summary.payable) : "—"}
                    {q.summary?.options && q.summary.options > 1 && q.status !== "accepted" ? <span className="muted"> · {q.summary.options} options</span> : null}
                  </td>
                  <td className="num">{q.summary?.marginPercent != null ? pct(q.summary.marginPercent) : "—"}</td>
                  <td>{when(q.updatedAt)}</td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    No quotes match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
